import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
  arrayUnion,
  limit,
  deleteDoc,
  CollectionReference,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { convertToBase64 } from '../utils/imageUtils';
import { InventoryItem, InventoryTransaction, TransactionType, Warehouse, WarehouseQuantity } from '../utils/types';
import { uploadImage, getImageIdFromUrl } from './cloudinaryService';

// Helper function to ensure Firestore is available
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  return db;
};

// دالة لتنظيف البيانات من undefined قبل الحفظ في Firestore
const cleanUndefined = (obj: any): any => {
  const cleaned: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

const getInventoryCollection = () => {
  return collection(ensureFirestore(), 'inventory');
};

const getWarehousesCollection = () => {
  return collection(ensureFirestore(), 'warehouses');
};

const getTransactionsCollection = () => {
  return collection(ensureFirestore(), 'inventoryTransactions');
};

// Create a new warehouse
export const createWarehouse = async (name: string, location?: string, branchId?: string, branchName?: string): Promise<string> => {
  try {
    console.log(`createWarehouse: Creating warehouse with name: ${name}, location: ${location}, branchId: ${branchId}, branchName: ${branchName}`);

    // التحقق من وجود اسم للمخزن
    if (!name || name.trim() === '') {
      throw new Error('اسم المخزن مطلوب');
    }

    // التحقق من عدم وجود مخزن بنفس الاسم
    try {
      const existingWarehouses = await getAllWarehouses();
      const existingWarehouse = existingWarehouses.find(w => w.name.trim().toLowerCase() === name.trim().toLowerCase());

      if (existingWarehouse) {
        console.log(`createWarehouse: Warehouse with name ${name} already exists with ID: ${existingWarehouse.id}`);
        return existingWarehouse.id; // إرجاع معرف المخزن الموجود بدلاً من إنشاء مخزن جديد
      }
    } catch (checkError) {
      console.warn('Error checking for existing warehouses:', checkError);
      // استمر في إنشاء المخزن حتى لو فشل التحقق
    }

    const warehouseData: Warehouse = {
      id: '',
      name: name.trim(),
      location: location || '',
      branchId: branchId || '',
      branchName: branchName || '',
      createdAt: new Date(),
      updatedAt: new Date(),
      isMain: true // تعيين المخزن كمخزن رئيسي افتراضيًا
    };

    console.log('createWarehouse: Warehouse data prepared:', warehouseData);

    const db = ensureFirestore();
    console.log('createWarehouse: Firestore instance:', db ? 'Available' : 'Not available');

    const warehousesCollection = getWarehousesCollection();
    console.log('createWarehouse: Got warehouses collection');

    console.log('createWarehouse: Adding warehouse to Firestore');
    const warehouseRef = await addDoc(warehousesCollection, warehouseData);
    console.log(`createWarehouse: Warehouse created with ID: ${warehouseRef.id}`);

    // تحديث المخزن بمعرفه الجديد
    try {
      await updateDoc(warehouseRef, { id: warehouseRef.id });
      console.log(`createWarehouse: Updated warehouse with its ID: ${warehouseRef.id}`);
    } catch (updateError) {
      console.warn(`createWarehouse: Failed to update warehouse with its ID: ${warehouseRef.id}`, updateError);
      // استمر حتى لو فشل التحديث
    }

    return warehouseRef.id;
  } catch (error) {
    console.error('Error creating warehouse:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

// Get all warehouses
export const getAllWarehouses = async (): Promise<Warehouse[]> => {
  try {
    console.log('getAllWarehouses: Starting to fetch warehouses');
    const warehousesCollection = getWarehousesCollection();
    console.log('getAllWarehouses: Got warehouses collection');

    const q = query(warehousesCollection, orderBy('name'));
    console.log('getAllWarehouses: Created query');


    const querySnapshot = await getDocs(q);
    console.log(`getAllWarehouses: Got ${querySnapshot.size} warehouses`);

    const warehouses = querySnapshot.docs.map(docSnapshot => {
      const data = docSnapshot.data();
      return {
        id: docSnapshot.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date()
      } as Warehouse;
    });

    console.log('getAllWarehouses: Processed warehouses data', warehouses);
    return warehouses;
  } catch (error) {
    console.error('Error getting warehouses:', error);
    return [];
  }
};

// Add a new inventory item
export const addInventoryItem = async (item: InventoryItem): Promise<string> => {
  try {
    // التحقق من صحة البيانات الأساسية
    if (!item.name || item.name.trim().length === 0) {
      throw new Error('اسم المنتج مطلوب');
    }

    if (!item.category || item.category.trim().length === 0) {
      throw new Error('فئة المنتج مطلوبة');
    }

    if (item.purchasePrice === undefined || item.purchasePrice < 0) {
      throw new Error('سعر الشراء غير صالح');
    }

    if (item.sellingPrice === undefined || item.sellingPrice < 0) {
      throw new Error('سعر البيع غير صالح');
    }

    // التحقق من صحة الكميات في المخازن
    if (!item.warehouseQuantities || item.warehouseQuantities.length === 0) {
      throw new Error('يجب تحديد الكميات في المخازن');
    }

    // التحقق من صحة كل كمية في المخازن
    let totalQuantity = 0;
    for (const wq of item.warehouseQuantities) {
      if (!wq.warehouseId || !wq.warehouseName) {
        throw new Error('معرف واسم المخزن مطلوبان لكل كمية');
      }
      if (wq.quantity === undefined || wq.quantity < 0) {
        throw new Error('الكمية في المخزن غير صالحة');
      }
      totalQuantity += wq.quantity;
    }

    // التحقق من وجود المخازن في قاعدة البيانات
    for (let i = 0; i < item.warehouseQuantities.length; i++) {
      const wq = item.warehouseQuantities[i];
      console.log(`Checking if warehouse exists: ${wq.warehouseId} (${wq.warehouseName})`);

      if (!wq.warehouseId) {
        console.error('Warehouse ID is empty or undefined');
        throw new Error('معرف المخزن غير صالح');
      }

      try {
        const warehouseDoc = await getDoc(doc(ensureFirestore(), 'warehouses', wq.warehouseId));

        if (!warehouseDoc.exists()) {
          console.error(`Warehouse with ID ${wq.warehouseId} does not exist`);

          // إذا كان المخزن هو المخزن الرئيسي، قم بإنشائه تلقائيًا
          if (wq.warehouseName === 'المخزن الرئيسي' || wq.warehouseName.toLowerCase().includes('main') || wq.warehouseName.toLowerCase().includes('default')) {
            console.log(`Creating default warehouse with name: ${wq.warehouseName}`);
            try {
              // إنشاء المخزن الرئيسي
              const newWarehouseId = await createWarehouse(wq.warehouseName, 'الموقع الافتراضي');
              console.log(`Created default warehouse with ID: ${newWarehouseId}`);

              // تحديث معرف المخزن في المنتج
              item.warehouseQuantities[i].warehouseId = newWarehouseId;

              // التحقق من المخزن الجديد
              const newWarehouseDoc = await getDoc(doc(ensureFirestore(), 'warehouses', newWarehouseId));
              if (newWarehouseDoc.exists()) {
                console.log(`New warehouse ${newWarehouseId} exists, name: ${newWarehouseDoc.data().name}`);
                wq.warehouseName = newWarehouseDoc.data().name;
                continue; // الانتقال إلى المخزن التالي
              } else {
                throw new Error(`فشل في إنشاء المخزن الرئيسي`);
              }
            } catch (createError) {
              console.error('Error creating default warehouse:', createError);
              throw new Error(`فشل في إنشاء المخزن الرئيسي: ${createError instanceof Error ? createError.message : 'خطأ غير معروف'}`);
            }
          } else {
            // إذا لم يكن المخزن الرئيسي، أرسل خطأ
            throw new Error(`المخزن ${wq.warehouseName || 'المحدد'} غير موجود`);
          }
        } else {
          console.log(`Warehouse ${wq.warehouseId} exists, name: ${warehouseDoc.data().name}`);

          // تحديث اسم المخزن من قاعدة البيانات للتأكد من صحته
          wq.warehouseName = warehouseDoc.data().name;
        }
      } catch (error) {
        console.error(`Error checking warehouse ${wq.warehouseId}:`, error);
        throw new Error(`حدث خطأ أثناء التحقق من المخزن: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
      }
    }

    // إنشاء معرف فريد للمنتج
    const sku = item.sku || `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // التأكد من أن جميع الحقول لها قيم صالحة لـ Firestore
    // لا يمكن أن تكون undefined
    const sanitizedItem = {
      ...item,
      name: item.name.trim(),
      category: item.category.trim(),
      subcategory: item.subcategory || '',
      subcategoryId: item.subcategoryId || '',
      categoryId: item.categoryId || '',
      barcode: item.barcode || '',
      description: item.description || '',
      supplier: item.supplier || '',
      image: item.image || '',
      imageUrl: item.imageUrl || item.image || '',
      imgbbUrl: item.imgbbUrl || '',
      imagekitUrl: item.imagekitUrl || '',
      additionalImages: item.additionalImages || [],
      featured: item.featured || false,
      bestSeller: item.bestSeller || false
    };

    // تحضير بيانات المنتج
    const inventoryData = {
      name: sanitizedItem.name,
      sku,
      barcode: sanitizedItem.barcode,
      category: sanitizedItem.category,
      categoryId: sanitizedItem.categoryId,
      subcategory: sanitizedItem.subcategory,
      subcategoryId: sanitizedItem.subcategoryId,
      purchasePrice: item.purchasePrice,
      sellingPrice: item.sellingPrice,
      unitPrice: item.unitPrice || 0, // إضافة سعر الوحدة
      // أسعار متعددة للمنتج
      posPrice: item.posPrice, // سعر نقطة البيع (POS)
      storePrice: item.storePrice, // سعر المتجر الإلكتروني
      wholesalePrice: item.wholesalePrice, // سعر الجملة
      quantity: totalQuantity,
      minQuantity: item.minQuantity || 0,
      description: sanitizedItem.description,
      supplier: sanitizedItem.supplier,
      warehouseQuantities: item.warehouseQuantities,
      totalQuantity,
      image: sanitizedItem.image,
      imageUrl: sanitizedItem.imageUrl,
      imgbbUrl: sanitizedItem.imgbbUrl, // إضافة رابط ImgBB
      imagekitUrl: sanitizedItem.imagekitUrl, // إضافة رابط ImageKit.io
      additionalImages: sanitizedItem.additionalImages, // إضافة الصور الإضافية
      showInStore: item.showInStore !== undefined ? item.showInStore : true,
      featured: sanitizedItem.featured, // منتج مميز
      bestSeller: sanitizedItem.bestSeller, // منتج الأكثر مبيعًا
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // إضافة المنتج إلى قاعدة البيانات
    const inventoryCollection = getInventoryCollection();
    const docRef = await addDoc(inventoryCollection, cleanUndefined(inventoryData));

    // إضافة معاملة مخزون لكل مخزن
    for (const wq of item.warehouseQuantities) {
      if (wq.quantity > 0) {
        const transactionData: InventoryTransaction = {
          id: '',
          type: TransactionType.RESTOCK,
          itemId: docRef.id,
          itemName: item.name,
          itemSku: sku,
          quantity: wq.quantity,
          fromWarehouseId: wq.warehouseId,
          fromWarehouseName: wq.warehouseName,
          notes: 'إضافة منتج جديد',
          createdById: 'system',
          createdByName: 'النظام',
          timestamp: new Date()
        };
        await addInventoryTransaction(transactionData);
      }
    }

    // حذف أي بيانات مخزنة محلياً
    localStorage.removeItem('inventory_cache');
    localStorage.removeItem('inventory_cache_expiry');

    console.log(`تم إضافة المنتج بنجاح، المعرف: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Error adding inventory item:', error);
    throw new Error(error instanceof Error ? error.message : 'فشل في إضافة عنصر المخزون');
  }
};

// Get warehouse by ID
export const getWarehouseById = async (id: string): Promise<Warehouse | null> => {
  try {
    if (!id) return null;

    const docRef = doc(ensureFirestore(), 'warehouses', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date()
      } as Warehouse;
    }

    return null;
  } catch (error) {
    console.error('Error getting warehouse by ID:', error);
    return null;
  }
};

// التأكد من وجود المخزن، وإنشائه إذا لم يكن موجودًا
export const ensureWarehouseExists = async (warehouseId: string, warehouseName: string): Promise<string> => {
  try {
    console.log(`ensureWarehouseExists: Checking warehouse ${warehouseId} (${warehouseName})`);

    // التحقق من وجود المخزن بالمعرف
    if (warehouseId) {
      const existingWarehouse = await getWarehouseById(warehouseId);
      if (existingWarehouse) {
        console.log(`ensureWarehouseExists: Warehouse ${warehouseId} exists with name ${existingWarehouse.name}`);
        return warehouseId;
      }
    }

    // البحث عن المخزن بالاسم
    if (warehouseName) {
      const allWarehouses = await getAllWarehouses();
      const existingWarehouse = allWarehouses.find(w =>
        w.name.trim().toLowerCase() === warehouseName.trim().toLowerCase());

      if (existingWarehouse) {
        console.log(`ensureWarehouseExists: Found warehouse with name ${warehouseName}, ID: ${existingWarehouse.id}`);
        return existingWarehouse.id;
      }
    }

    // إنشاء مخزن جديد إذا لم يكن موجودًا
    console.log(`ensureWarehouseExists: Creating new warehouse with name ${warehouseName}`);
    const newWarehouseId = await createWarehouse(
      warehouseName || 'المخزن الرئيسي',
      'الموقع الافتراضي'
    );

    console.log(`ensureWarehouseExists: Created new warehouse with ID ${newWarehouseId}`);
    return newWarehouseId;
  } catch (error) {
    console.error('Error in ensureWarehouseExists:', error);
    throw new Error(`فشل في التحقق من وجود المخزن: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
  }
};

// Get inventory item by ID
export const getInventoryItemById = async (id: string): Promise<InventoryItem | null> => {
  try {
    const docRef = doc(ensureFirestore(), 'inventory', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Asegurarse de que el precio sea un número
      const purchasePrice = Number(data.purchasePrice || data.costPrice || 0);
      const sellingPrice = Number(data.sellingPrice || data.price || 0);

      console.log('Raw item data from Firestore:', data);
      console.log('Extracted purchase price:', purchasePrice);
      console.log('Extracted selling price:', sellingPrice);
      console.log('Multiple prices from Firestore:', {
        posPrice: data.posPrice,
        storePrice: data.storePrice,
        wholesalePrice: data.wholesalePrice
      });

      // تحقق مما إذا كانت الصور الإضافية موجودة وهي مصفوفة
      const additionalImages = data.additionalImages || [];
      console.log('Additional images data from Firestore:', additionalImages);

      return {
        id: docSnap.id,
        name: data.name || '',
        sku: data.sku || '',
        barcode: data.barcode || '',
        category: data.category || '',
        categoryId: data.categoryId || '',
        subcategory: data.subcategory || '',
        subcategoryId: data.subcategoryId || '',
        purchasePrice: purchasePrice,
        sellingPrice: sellingPrice,
        // أسعار متعددة للمنتج
        posPrice: data.posPrice !== undefined ? Number(data.posPrice) : undefined,
        storePrice: data.storePrice !== undefined ? Number(data.storePrice) : undefined,
        wholesalePrice: data.wholesalePrice !== undefined ? Number(data.wholesalePrice) : undefined,
        quantity: data.quantity || 0,
        minQuantity: data.minQuantity || 0,
        description: data.description || '',
        supplier: data.supplier || '',
        warehouseId: data.warehouseId || 'default',
        warehouseName: data.warehouseName || 'المستودع الافتراضي',
        warehouseQuantities: data.warehouseQuantities || [],
        image: data.image || '',
        imageUrl: data.imageUrl || data.image || '',
        imgbbUrl: data.imgbbUrl || '',  // إضافة رابط ImgBB
        imagekitUrl: data.imagekitUrl || '', // إضافة رابط ImageKit.io
        additionalImages: additionalImages, // إضافة الصور الإضافية
        showInStore: data.showInStore !== undefined ? data.showInStore : true,
        featured: data.featured !== undefined ? data.featured : false,
        bestSeller: data.bestSeller !== undefined ? data.bestSeller : false,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as InventoryItem;
    }

    return null;
  } catch (error) {
    console.error('Error getting inventory item:', error);
    return null;
  }
};

// Get all inventory items
export const getAllInventoryItems = async (): Promise<InventoryItem[]> => {
  try {
    const inventoryCollectionRef = getInventoryCollection();
    const querySnapshot = await getDocs(inventoryCollectionRef);

    // تحويل البيانات من Firestore إلى نموذج InventoryItem
    const items = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        sku: data.sku || '',
        barcode: data.barcode || '',
        category: data.category || '',
        purchasePrice: data.purchasePrice || data.costPrice || 0,
        sellingPrice: data.sellingPrice || data.price || 0,
        unitPrice: data.unitPrice || 0, // إضافة سعر الوحدة
        // أسعار متعددة للمنتج
        posPrice: data.posPrice, // سعر نقطة البيع (POS)
        storePrice: data.storePrice, // سعر المتجر الإلكتروني
        wholesalePrice: data.wholesalePrice, // سعر الجملة
        quantity: data.quantity || 0,
        minQuantity: data.minQuantity || 0,
        description: data.description || '',
        supplier: data.supplier || '',
        warehouseId: data.warehouseId || 'default',
        warehouseName: data.warehouseName || 'المستودع الافتراضي',
        image: data.image || '',
        imageUrl: data.imageUrl || data.image || '',
        showInStore: data.showInStore !== undefined ? data.showInStore : true,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as InventoryItem;
    });

    // حذف أي بيانات مخزنة محلياً
    localStorage.removeItem('inventory_cache');
    localStorage.removeItem('inventory_cache_expiry');

    return items;
  } catch (error) {
    console.error('Error getting inventory items:', error);
    return [];
  }
};

// Get inventory items by category
export const getInventoryItemsByCategory = async (category: string): Promise<InventoryItem[]> => {
  try {
    const inventoryCollectionRef = getInventoryCollection();
    const q = query(inventoryCollectionRef, where('category', '==', category));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as InventoryItem;
    });
  } catch (error) {
    console.error('Error getting inventory items by category:', error);
    return [];
  }
};

// Get low stock items
export const getLowStockItems = async (): Promise<InventoryItem[]> => {
  try {
    const inventoryCollectionRef = getInventoryCollection();
    const querySnapshot = await getDocs(inventoryCollectionRef);

    const lowStockItems = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          sku: data.sku || '',
          barcode: data.barcode || '',
          category: data.category || '',
          purchasePrice: data.purchasePrice || data.costPrice || 0,
          sellingPrice: data.sellingPrice || data.price || 0,
          unitPrice: data.unitPrice || 0, // إضافة سعر الوحدة
          quantity: data.quantity || 0,
          minQuantity: data.minQuantity || 0,
          description: data.description || '',
          supplier: data.supplier || '',
          warehouseId: data.warehouseId || 'default',
          warehouseName: data.warehouseName || 'المستودع الافتراضي',
          image: data.image || '',
          imageUrl: data.imageUrl || data.image || '',
          showInStore: data.showInStore !== undefined ? data.showInStore : true,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        } as InventoryItem;
      })
      .filter(item =>
        item.minQuantity > 0 && item.quantity <= item.minQuantity
      );

    return lowStockItems;
  } catch (error) {
    console.error('Error getting low stock items:', error);
    return [];
  }
};

// Update inventory item
export const updateInventoryItem = async (id: string, item: InventoryItem): Promise<void> => {
  try {
    const docRef = doc(ensureFirestore(), 'inventory', id);

    // حساب الكمية الإجمالية من كميات المخازن
    let totalQuantity = 0;

    // تحقق من واجهة الكميات في المخازن المختلفة
    if (item.warehouseQuantities && item.warehouseQuantities.length > 0) {
      // حساب الكمية الإجمالية من خلال جمع كميات المخازن
      totalQuantity = item.warehouseQuantities.reduce((sum, wq) => sum + wq.quantity, 0);

      // التأكد من وجود معرف واسم المخزن لكل كمية
      for (const wq of item.warehouseQuantities) {
        if (!wq.warehouseName) {
          const warehouse = await getWarehouseById(wq.warehouseId);
          if (warehouse) {
            wq.warehouseName = warehouse.name;
          } else {
            wq.warehouseName = 'المستودع الافتراضي';
          }
        }
      }
    } else {
      // إذا لم يتم تحديد الكميات حسب المخزن، استخدم المخزن الافتراضي
      totalQuantity = item.quantity || 0;

      item.warehouseQuantities = [{
        warehouseId: item.warehouseId || 'default',
        warehouseName: item.warehouseName || 'المستودع الافتراضي',
        quantity: item.quantity || 0
      }];
    }

    // التأكد من أن جميع الحقول لها قيم صالحة لـ Firestore
    // لا يمكن أن تكون undefined
    const sanitizedItem = {
      ...item,
      subcategory: item.subcategory || '',
      subcategoryId: item.subcategoryId || '',
      categoryId: item.categoryId || '',
      barcode: item.barcode || '',
      description: item.description || '',
      supplier: item.supplier || '',
      image: item.image || '',
      imageUrl: item.imageUrl || ''
    };

    // تحويل البيانات لتتوافق مع النموذج وضمان عدم وجود قيم undefined
    const inventoryData = {
      name: sanitizedItem.name || '',
      sku: sanitizedItem.sku || '',
      barcode: sanitizedItem.barcode || '',
      category: sanitizedItem.category || '',
      categoryId: sanitizedItem.categoryId || '',
      subcategory: sanitizedItem.subcategory || '',
      subcategoryId: sanitizedItem.subcategoryId || '',
      purchasePrice: sanitizedItem.purchasePrice || 0,
      sellingPrice: sanitizedItem.sellingPrice || 0,
      unitPrice: sanitizedItem.unitPrice || 0, // إضافة سعر الوحدة
      // أسعار متعددة للمنتج
      posPrice: sanitizedItem.posPrice, // سعر نقطة البيع (POS)
      storePrice: sanitizedItem.storePrice, // سعر المتجر الإلكتروني
      wholesalePrice: sanitizedItem.wholesalePrice, // سعر الجملة
      quantity: totalQuantity, // تحديث الكمية الإجمالية
      minQuantity: sanitizedItem.minQuantity || 0,
      description: sanitizedItem.description || '',
      supplier: sanitizedItem.supplier || '',
      warehouseId: sanitizedItem.warehouseId || 'default',
      warehouseName: sanitizedItem.warehouseName || 'المستودع الافتراضي',
      warehouseQuantities: item.warehouseQuantities || [], // تحديث الكميات في المخازن المختلفة
      totalQuantity: totalQuantity, // تحديث الكمية الإجمالية مرة أخرى للتوافق
      image: sanitizedItem.image || '',
      imageUrl: sanitizedItem.imageUrl || '',
      imgbbUrl: sanitizedItem.imgbbUrl || '', // إضافة رابط ImgBB
      imagekitUrl: sanitizedItem.imagekitUrl || '', // إضافة رابط ImageKit.io
      additionalImages: sanitizedItem.additionalImages || [], // إضافة الصور الإضافية
      showInStore: sanitizedItem.showInStore !== undefined ? sanitizedItem.showInStore : true,
      featured: sanitizedItem.featured !== undefined ? sanitizedItem.featured : false, // منتج مميز
      bestSeller: sanitizedItem.bestSeller !== undefined ? sanitizedItem.bestSeller : false, // منتج الأكثر مبيعًا
      updatedAt: new Date()
    };

    // تسجيل البيانات للتأكد من عدم وجود قيم undefined
    console.log('بيانات المنتج المراد تحديثه (بعد التنظيف):', JSON.stringify(inventoryData));

    console.log('بيانات المنتج المراد تحديثه:', inventoryData);

    await updateDoc(docRef, cleanUndefined(inventoryData));

    console.log(`تم تحديث المنتج بنجاح، المعرف: ${id}`);
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw new Error('فشل في تحديث عنصر المخزون: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

// Delete inventory item
export const deleteInventoryItem = async (id: string): Promise<void> => {
  try {
    const docRef = doc(ensureFirestore(), 'inventory', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting inventory item:', error);
    throw new Error('فشل في حذف عنصر المخزون');
  }
};

// Update inventory quantity
export const updateInventoryQuantity = async (id: string, quantityChange: number): Promise<void> => {
  try {
    const docRef = doc(ensureFirestore(), 'inventory', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('لم يتم العثور على العنصر');
    }

    const currentQuantity = docSnap.data().quantity || 0;
    const newQuantity = currentQuantity + quantityChange;

    if (newQuantity < 0) {
      throw new Error('لا يمكن أن تكون الكمية أقل من الصفر');
    }

    await updateDoc(docRef, {
      quantity: newQuantity,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating inventory quantity:', error);
    throw error;
  }
};

// Search inventory items
export const searchInventoryItems = async (searchTerm: string): Promise<InventoryItem[]> => {
  try {
    const inventoryCollectionRef = getInventoryCollection();
    const querySnapshot = await getDocs(inventoryCollectionRef);

    const searchTermLower = searchTerm.toLowerCase().trim();
    const searchTerms = searchTermLower.split(/\s+/).filter(term => term.length > 0);

    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          sku: data.sku || '',
          barcode: data.barcode || '',
          category: data.category || '',
          subcategory: data.subcategory || '',
          purchasePrice: data.purchasePrice || data.costPrice || 0,
          sellingPrice: data.sellingPrice || data.price || 0,
          unitPrice: data.unitPrice || 0,
          quantity: data.quantity || 0,
          minQuantity: data.minQuantity || 0,
          description: data.description || '',
          supplier: data.supplier || '',
          warehouseId: data.warehouseId || 'default',
          warehouseName: data.warehouseName || 'المستودع الافتراضي',
          image: data.image || '',
          imageUrl: data.imageUrl || data.image || '',
          showInStore: data.showInStore !== undefined ? data.showInStore : true,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        } as InventoryItem;
      })
      .filter(item => {
        const searchText = [
          item.name,
          item.sku,
          item.barcode,
          item.category,
          item.subcategory,
          item.description,
          item.supplier
        ].filter(Boolean).join(' ').toLowerCase();

        if (searchTerms.length === 0) return false;

        return searchTerms.every(term => searchText.includes(term));
      });
  } catch (error) {
    console.error('Error searching inventory items:', error);
    return [];
  }
};

// Get inventory categories
export const getInventoryCategories = async (): Promise<string[]> => {
  try {
    const inventoryCollectionRef = getInventoryCollection();
    const querySnapshot = await getDocs(inventoryCollectionRef);

    const categories = new Set<string>();
    querySnapshot.docs.forEach(doc => {
      const category = doc.data().category;
      if (category) {
        categories.add(category);
      }
    });

    return Array.from(categories);
  } catch (error) {
    console.error('Error getting inventory categories:', error);
    return [];
  }
};

// Update inventory item quantity directly
export const updateInventoryItemQuantity = async (id: string, newQuantity: number): Promise<void> => {
  try {
    const docRef = doc(ensureFirestore(), 'inventory', id);

    // التأكد من أن الكمية غير سالبة
    if (newQuantity < 0) {
      throw new Error('لا يمكن أن تكون الكمية أقل من الصفر');
    }

    await updateDoc(docRef, {
      quantity: newQuantity,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating inventory item quantity:', error);
    throw error;
  }
};

// Transfer inventory item between warehouses
export const transferInventoryItem = async (
  itemId: string,
  fromWarehouseId: string,
  toWarehouseId: string,
  quantity: number
): Promise<void> => {
  try {
    // Get item details
    const itemRef = doc(ensureFirestore(), 'inventory', itemId);
    const itemDoc = await getDoc(itemRef);

    if (!itemDoc.exists()) {
      throw new Error('لم يتم العثور على العنصر');
    }

    const itemData = itemDoc.data() as InventoryItem;

    if (itemData.quantity < quantity) {
      throw new Error('الكمية المطلوبة للنقل أكبر من الكمية الموجودة');
    }

    // Get target warehouse
    const toWarehouseDoc = await getDoc(doc(ensureFirestore(), 'warehouses', toWarehouseId));

    if (!toWarehouseDoc.exists()) {
      throw new Error('لم يتم العثور على المستودع الهدف');
    }

    const toWarehouseData = toWarehouseDoc.data() as Warehouse;

    // Check if item already exists in target warehouse
    const targetItemQuery = query(
      getInventoryCollection(),
      where('sku', '==', itemData.sku),
      where('warehouseId', '==', toWarehouseId)
    );
    const targetItemSnapshot = await getDocs(targetItemQuery);

    // Update source item quantity
    await updateDoc(itemRef, {
      quantity: itemData.quantity - quantity,
      updatedAt: new Date()
    });

    if (targetItemSnapshot.empty) {
      // Create new item in target warehouse
      const newItemData = { ...itemData };
      newItemData.quantity = quantity;
      newItemData.warehouseId = toWarehouseId;
      newItemData.warehouseName = toWarehouseData.name;
      newItemData.createdAt = new Date();
      newItemData.updatedAt = new Date();
      // Safely delete the id property
      const { id, ...newItemWithoutId } = newItemData;

      await addDoc(getInventoryCollection(), newItemWithoutId);
    } else {
      // Update existing item in target warehouse
      const targetItemRef = doc(ensureFirestore(), 'inventory', targetItemSnapshot.docs[0].id);
      await updateDoc(targetItemRef, {
        quantity: increment(quantity),
        updatedAt: new Date()
      });
    }

    // Add transaction record
    const transactionData: InventoryTransaction = {
      id: '',
      type: TransactionType.TRANSFER,
      itemId,
      itemName: itemData.name,
      itemSku: itemData.sku,
      quantity,
      fromWarehouseId,
      fromWarehouseName: itemData.warehouseName,
      toWarehouseId,
      toWarehouseName: toWarehouseData.name,
      notes: `نقل من المستودع ${itemData.warehouseName} إلى المستودع ${toWarehouseData.name}`,
      createdById: 'system', // يمكن تعديلها لتستخدم هوية المستخدم الحالي
      createdByName: 'النظام', // يمكن تعديلها لتستخدم اسم المستخدم الحالي
      timestamp: new Date()
    };

    const transactionRef = await addDoc(getTransactionsCollection(), transactionData);

    return;
  } catch (error) {
    console.error('Error transferring inventory item:', error);
    throw error;
  }
};

// Add inventory transaction
export const addInventoryTransaction = async (transaction: InventoryTransaction): Promise<string> => {
  try {
    // تسجيل المعاملة
    const transactionRef = await addDoc(getTransactionsCollection(), {
      ...transaction,
      timestamp: new Date()
    });

    return transactionRef.id;
  } catch (error) {
    console.error('Error adding inventory transaction:', error);
    throw error;
  }
};

// Get inventory transactions
export const getInventoryTransactions = async (
  itemId?: string,
  warehouseId?: string,
  limitCount: number = 100
): Promise<InventoryTransaction[]> => {
  try {
    let q;

    if (itemId && warehouseId) {
      q = query(
        getTransactionsCollection(),
        where('itemId', '==', itemId),
        where('fromWarehouseId', '==', warehouseId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else if (itemId) {
      q = query(
        getTransactionsCollection(),
        where('itemId', '==', itemId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else if (warehouseId) {
      q = query(
        getTransactionsCollection(),
        where('fromWarehouseId', '==', warehouseId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else {
      q = query(
        getTransactionsCollection(),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Ensure all required properties are included
      return {
        id: doc.id,
        itemId: data.itemId || '',
        itemName: data.itemName || '',
        quantity: data.quantity || 0,
        type: data.type || TransactionType.ADJUSTMENT,
        timestamp: data.timestamp instanceof Timestamp
          ? data.timestamp.toDate()
          : new Date(data.timestamp),
        fromWarehouseId: data.fromWarehouseId,
        fromWarehouseName: data.fromWarehouseName,
        toWarehouseId: data.toWarehouseId,
        toWarehouseName: data.toWarehouseName,
        createdById: data.createdById,
        createdByName: data.createdByName,
        notes: data.notes,
        itemSku: data.itemSku
      };
    });
  } catch (error) {
    console.error('Error getting inventory transactions:', error);
    return [];
  }
};

// Get total inventory value
export const getTotalInventoryValue = async (): Promise<number> => {
  try {
    const items = await getAllInventoryItems();

    return items.reduce((total, item) => {
      return total + (item.purchasePrice * item.quantity);
    }, 0);
  } catch (error) {
    console.error('Error calculating total inventory value:', error);
    return 0;
  }
};

// Upload an image for an inventory item
export const uploadInventoryImage = async (file: File, itemId: string): Promise<string> => {
  try {
    // استخدام الخدمة الموحدة
    console.log('جاري رفع الصورة للمنتج...');

    const { uploadUnifiedImage } = await import('./unifiedImageService');
    const result = await uploadUnifiedImage({
      file: file,
      context: 'inventory',
      contextId: itemId,
      folder: `inventory/${itemId}`
    });

    console.log('تم رفع الصورة بنجاح:', result.url);

    // تحديث العنصر بعنوان URL للصورة
    const docRef = doc(ensureFirestore(), 'inventory', itemId);
    await updateDoc(docRef, {
      image: result.url,
      imageUrl: result.url,
      imgbbUrl: result.imgbbUrl || '',
      imagekitUrl: result.imagekitUrl || '',
      updatedAt: new Date()
    });

    return result.url;
  } catch (error) {
    console.error('خطأ في رفع صورة المنتج:', error);
    throw new Error('فشل في رفع صورة المنتج: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

// تحديث جميع العناصر بفئة جديدة (لإزالة التكرارات)
export const updateInventoryItemsCategory = async (oldCategoryName: string, newCategoryName: string, newCategoryId: string): Promise<number> => {
  try {
    // الحصول على العناصر التي تحتوي على الفئة القديمة
    const inventoryCollection = getInventoryCollection();
    const q = query(
      inventoryCollection,
      where('category', '==', oldCategoryName)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`لا توجد عناصر مخزون بفئة "${oldCategoryName}"`);
      return 0;
    }

    // تحديث كل عنصر
    const updatePromises = querySnapshot.docs.map(async (docSnapshot) => {
      const itemRef = doc(ensureFirestore(), 'inventory', docSnapshot.id);
      await updateDoc(itemRef, {
        category: newCategoryName,
        categoryId: newCategoryId,
        updatedAt: new Date()
      });
    });

    // انتظار انتهاء جميع التحديثات
    await Promise.all(updatePromises);

    console.log(`تم تحديث ${querySnapshot.size} عنصر من فئة "${oldCategoryName}" إلى فئة "${newCategoryName}"`);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error updating inventory category:', error);
    throw new Error('فشل في تحديث فئة عناصر المخزون');
  }
};

// حذف فئة من كافة عناصر المخزون
export const clearCategoryFromInventory = async (categoryName: string): Promise<number> => {
  try {
    // الحصول على العناصر التي تحتوي على الفئة المحددة
    const inventoryCollection = getInventoryCollection();
    const q = query(
      inventoryCollection,
      where('category', '==', categoryName)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`لا توجد عناصر مخزون بفئة "${categoryName}"`);
      return 0;
    }

    // تحديث كل عنصر لإزالة الفئة
    const updatePromises = querySnapshot.docs.map(async (docSnapshot) => {
      const itemRef = doc(ensureFirestore(), 'inventory', docSnapshot.id);
      await updateDoc(itemRef, {
        category: '',
        categoryId: '',
        subcategory: '',
        subcategoryId: '',
        updatedAt: new Date()
      });
    });

    // انتظار انتهاء جميع التحديثات
    await Promise.all(updatePromises);

    console.log(`تم إزالة الفئة "${categoryName}" من ${querySnapshot.size} عنصر مخزون`);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error clearing category from inventory:', error);
    throw new Error('فشل في إزالة الفئة من عناصر المخزون');
  }
};

// Update inventory quantity in specific warehouse
export const updateInventoryQuantityInWarehouse = async (
  itemId: string,
  warehouseId: string,
  quantityChange: number,
  userId?: string,
  notes?: string
): Promise<void> => {
  try {
    console.log(`تحديث كمية العنصر ${itemId} في المخزن ${warehouseId} بقيمة ${quantityChange}`);

    // التحقق من صلاحية المستخدم للوصول إلى هذا المخزن
    if (userId) {
      const userHasAccess = await checkUserWarehouseAccess(userId, warehouseId);
      if (!userHasAccess) {
        throw new Error('ليس لديك صلاحية للوصول إلى هذا المخزن');
      }
    }

    const docRef = doc(ensureFirestore(), 'inventory', itemId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('لم يتم العثور على العنصر');
    }

    const itemData = docSnap.data() as InventoryItem;
    let warehouseQuantities = itemData.warehouseQuantities || [];
    const currentTotalQuantity = itemData.quantity || 0;

    // البحث عن المخزن المحدد
    const warehouseIndex = warehouseQuantities.findIndex(wq => wq.warehouseId === warehouseId);

    if (warehouseIndex === -1) {
      // إذا لم يتم العثور على المخزن، أضف قيمة جديدة
      const warehouse = await getWarehouseById(warehouseId);

      if (!warehouse) {
        throw new Error('لم يتم العثور على المخزن');
      }

      // التحقق من أن الكمية الجديدة ليست سالبة
      if (quantityChange < 0) {
        throw new Error('لا يمكن خصم كمية من مخزن لا يحتوي على هذا العنصر');
      }

      warehouseQuantities.push({
        warehouseId,
        warehouseName: warehouse.name,
        quantity: quantityChange
      });
    } else {
      // تحديث الكمية في المخزن المحدد
      const currentWarehouseQuantity = warehouseQuantities[warehouseIndex].quantity || 0;
      const newWarehouseQuantity = currentWarehouseQuantity + quantityChange;

      // التحقق من أن الكمية الجديدة ليست سالبة
      if (newWarehouseQuantity < 0) {
        throw new Error('لا يمكن أن تكون الكمية في المخزن أقل من الصفر');
      }

      warehouseQuantities[warehouseIndex].quantity = newWarehouseQuantity;
    }

    // حساب الكمية الإجمالية الجديدة
    const newTotalQuantity = warehouseQuantities.reduce((sum, wq) => sum + wq.quantity, 0);

    // طباعة معلومات التحديث للتشخيص
    console.log(`الكمية الحالية في المخزن: ${warehouseIndex !== -1 ? warehouseQuantities[warehouseIndex].quantity : 0}`);
    console.log(`الكمية الإجمالية الجديدة: ${newTotalQuantity}`);

    // تحديث العنصر في قاعدة البيانات
    await updateDoc(docRef, {
      warehouseQuantities,
      quantity: newTotalQuantity,
      totalQuantity: newTotalQuantity,
      updatedAt: new Date()
    });

    // إضافة معاملة مخزون
    const transactionType = quantityChange > 0 ? TransactionType.RESTOCK : TransactionType.SALE;

    const warehouseData = await getWarehouseById(warehouseId);
    const warehouseName = warehouseData?.name || 'مخزن غير معروف';

    const transactionData: InventoryTransaction = {
      id: '',
      type: transactionType,
      itemId,
      itemName: itemData.name,
      itemSku: itemData.sku,
      quantity: Math.abs(quantityChange),
      fromWarehouseId: warehouseId,
      fromWarehouseName: warehouseName,
      notes: notes || `تعديل كمية بقيمة ${quantityChange > 0 ? '+' : ''}${quantityChange}`,
      createdById: userId || 'system',
      createdByName: 'النظام',
      timestamp: new Date()
    };

    await addInventoryTransaction(transactionData);

    console.log(`تم تحديث كمية العنصر ${itemData.name} في المخزن ${warehouseName} بنجاح. الكمية الجديدة: ${newTotalQuantity}`);
  } catch (error) {
    console.error('Error updating inventory quantity in warehouse:', error);
    throw error;
  }
};

// Check if user has access to warehouse
export const checkUserWarehouseAccess = async (userId: string, warehouseId: string): Promise<boolean> => {
  try {
    console.log(`🔐 checkUserWarehouseAccess: Checking access for user ${userId} to warehouse ${warehouseId}`);
    
    // الحصول على معلومات المستخدم
    const userDoc = await getDoc(doc(ensureFirestore(), 'users', userId));

    if (!userDoc.exists()) {
      console.log('❌ User document not found');
      return false;
    }

    const userData = userDoc.data();
    console.log('👤 User data:', { role: userData.role, allowedWarehouses: userData.allowedWarehouses, branchId: userData.branchId });

    // إذا كان المستخدم مدير، فلديه وصول إلى جميع المخازن
    if (userData.role === 'ADMIN' || userData.role === 'admin' || userData.role === 'manager') {
      console.log('✅ Admin/Manager user - access granted');
      return true;
    }

    // إذا لم تكن هناك قائمة مخازن محددة، السماح بالوصول (للتوافق مع النظام القديم)
    if (!userData.allowedWarehouses || userData.allowedWarehouses.length === 0) {
      console.log('✅ No allowedWarehouses specified - granting access for backward compatibility');
      return true;
    }

    // التحقق من قائمة المخازن المسموح بها
    const allowedWarehouses = userData.allowedWarehouses || [];
    if (allowedWarehouses.includes(warehouseId)) {
      console.log('✅ Warehouse found in user allowedWarehouses');
      return true;
    }

    // التحقق من الفرع المرتبط بالمستخدم
    if (userData.branchId) {
      const branchDoc = await getDoc(doc(ensureFirestore(), 'branches', userData.branchId));

      if (branchDoc.exists()) {
        const branchData = branchDoc.data();
        const branchWarehouses = branchData.warehouseIds || [];

        if (branchWarehouses.includes(warehouseId)) {
          console.log('✅ Warehouse found in user branch warehouses');
          return true;
        }
      }
    }

    console.log('❌ Warehouse access denied');
    return false;
  } catch (error) {
    console.error('Error checking user warehouse access:', error);
    // في حالة الخطأ، السماح بالوصول لتجنب منع المستخدمين من العمل
    console.log('⚠️ Error occurred, granting access to prevent blocking users');
    return true;
  }
};

// Check inventory quantity in warehouse with access control
export const getInventoryQuantityInWarehouse = async (itemId: string, warehouseId: string): Promise<number> => {
  try {
    const itemDoc = await getDoc(doc(ensureFirestore(), 'inventory', itemId));
    if (!itemDoc.exists()) {
      return 0;
    }

    const itemData = itemDoc.data() as InventoryItem;
    const warehouseQuantities = itemData.warehouseQuantities || [];

    const warehouseItem = warehouseQuantities.find((wq: WarehouseQuantity) => wq.warehouseId === warehouseId);
    return warehouseItem ? warehouseItem.quantity : 0;
  } catch (error) {
    console.error('Error getting inventory quantity in warehouse:', error);
    return 0;
  }
};

// Update inventory quantity in warehouse with access control
export const updateInventoryQuantityInWarehouseWithAccess = async (
  itemId: string,
  warehouseId: string,
  quantityChange: number,
  userId: string,
  notes?: string
): Promise<void> => {
  try {
    // Check if user has access to this warehouse
    const hasAccess = await checkUserWarehouseAccess(userId, warehouseId);
    if (!hasAccess) {
      throw new Error(`ليس لديك صلاحية للوصول إلى هذا المخزن`);
    }

    await updateInventoryQuantityInWarehouse(itemId, warehouseId, quantityChange, userId, notes);
  } catch (error) {
    console.error('Error updating inventory quantity in warehouse with access control:', error);
    throw error;
  }
};

// Transfer inventory item between warehouses with access control
export const transferInventoryItemWithAccess = async (
  itemId: string,
  fromWarehouseId: string,
  toWarehouseId: string,
  quantity: number,
  userId: string
): Promise<void> => {
  try {
    // Check if user has access to source warehouse
    const hasAccessToSource = await checkUserWarehouseAccess(userId, fromWarehouseId);
    if (!hasAccessToSource) {
      throw new Error(`ليس لديك صلاحية للوصول إلى المخزن المصدر`);
    }

    // Check if user has access to target warehouse
    const hasAccessToTarget = await checkUserWarehouseAccess(userId, toWarehouseId);
    if (!hasAccessToTarget) {
      throw new Error(`ليس لديك صلاحية للوصول إلى المخزن الهدف`);
    }

    await transferInventoryItem(itemId, fromWarehouseId, toWarehouseId, quantity);
  } catch (error) {
    console.error('Error transferring inventory item with access control:', error);
    throw error;
  }
};

// تحديث بيانات المخزن
export const updateWarehouse = async (warehouseId: string, updates: Partial<Warehouse>): Promise<void> => {
  try {
    console.log(`updateWarehouse: Updating warehouse ${warehouseId} with:`, updates);

    const db = ensureFirestore();
    console.log('updateWarehouse: Firestore instance:', db ? 'Available' : 'Not available');

    // التحقق من وجود المخزن قبل التحديث
    const warehouseRef = doc(db, 'warehouses', warehouseId);
    const warehouseDoc = await getDoc(warehouseRef);

    if (!warehouseDoc.exists()) {
      console.error(`updateWarehouse: Warehouse with ID ${warehouseId} does not exist`);
      throw new Error(`المخزن غير موجود`);
    }

    console.log(`updateWarehouse: Warehouse exists, proceeding with update`);
    const updateData = {
      ...updates,
      updatedAt: new Date()
    };

    console.log('updateWarehouse: Update data:', updateData);
    await updateDoc(warehouseRef, updateData);
    console.log(`updateWarehouse: Warehouse ${warehouseId} updated successfully`);
  } catch (error) {
    console.error('Error updating warehouse:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

// حذف جميع المخازن
export const deleteAllWarehouses = async (): Promise<void> => {
  try {
    console.log('deleteAllWarehouses: Starting to delete all warehouses');
    const warehouses = await getAllWarehouses();
    console.log(`deleteAllWarehouses: Found ${warehouses.length} warehouses to delete`);

    // حذف المخازن مباشرة من Firestore بدلاً من استخدام دالة deleteWarehouse
    const db = ensureFirestore();

    for (const warehouse of warehouses) {
      try {
        console.log(`deleteAllWarehouses: Deleting warehouse ${warehouse.id} (${warehouse.name})`);
        const warehouseRef = doc(db, 'warehouses', warehouse.id);
        await deleteDoc(warehouseRef);
        console.log(`deleteAllWarehouses: Successfully deleted warehouse ${warehouse.id}`);
      } catch (warehouseError) {
        console.error(`Error deleting warehouse ${warehouse.id}:`, warehouseError);
        // استمر في محاولة حذف المخازن الأخرى
      }
    }

    console.log('deleteAllWarehouses: Successfully deleted all warehouses');
  } catch (error) {
    console.error('Error deleting all warehouses:', error);
    throw new Error('فشل في حذف جميع المخازن');
  }
};

// إنشاء مخزن افتراضي جديد
export const createDefaultWarehouse = async (): Promise<string> => {
  try {
    console.log('createDefaultWarehouse: Creating default warehouse');
    const warehouseId = await createWarehouse('المخزن الرئيسي', 'الموقع الافتراضي');
    console.log(`createDefaultWarehouse: Created default warehouse with ID ${warehouseId}`);
    return warehouseId;
  } catch (error) {
    console.error('Error creating default warehouse:', error);
    throw new Error('فشل في إنشاء المخزن الافتراضي');
  }
};

// إعادة تعيين المخازن (حذف الكل وإنشاء مخزن افتراضي)
export const resetWarehouses = async (): Promise<string> => {
  try {
    console.log('resetWarehouses: Starting warehouse reset');

    // محاولة حذف جميع المخازن
    try {
      await deleteAllWarehouses();
      console.log('resetWarehouses: All warehouses deleted successfully');
    } catch (deleteError) {
      console.error('resetWarehouses: Error deleting warehouses:', deleteError);
      console.log('resetWarehouses: Continuing with creating default warehouse...');
      // استمر حتى لو فشل حذف المخازن
    }

    // إنشاء مخزن افتراضي جديد
    const defaultWarehouseId = await createDefaultWarehouse();
    console.log(`resetWarehouses: Reset completed, new default warehouse ID: ${defaultWarehouseId}`);
    return defaultWarehouseId;
  } catch (error) {
    console.error('Error resetting warehouses:', error);
    throw new Error('فشل في إعادة تعيين المخازن');
  }
};

// حذف مخزن
export const deleteWarehouse = async (warehouseId: string, force: boolean = false): Promise<void> => {
  try {
    console.log(`Attempting to delete warehouse ${warehouseId} with force=${force}`);

    // التحقق من وجود منتجات في المخزن
    if (!force) {
      console.log('Force delete is OFF, checking constraints...');

      // التحقق من وجود منتجات في المخزن
      const inventoryCollection = getInventoryCollection();
      const q = query(inventoryCollection);
      const querySnapshot = await getDocs(q);

      console.log(`Checking ${querySnapshot.size} inventory items for warehouse usage`);

      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        const warehouseQuantities = data.warehouseQuantities || [];

        const hasWarehouse = warehouseQuantities.some(
          (wq: any) => wq.warehouseId === warehouseId && wq.quantity > 0
        );

        if (hasWarehouse) {
          console.log(`Found inventory item ${docSnapshot.id} using this warehouse`);
          throw new Error('لا يمكن حذف المخزن لأنه يحتوي على منتجات. يجب نقل المنتجات أو حذفها أولاً.');
        }
      }

      // التحقق من ارتباط المخزن بفرع
      const branchesCollection = collection(ensureFirestore(), 'branches');
      const branchQuery = query(branchesCollection, where('warehouseIds', 'array-contains', warehouseId));
      const branchSnapshot = await getDocs(branchQuery);

      console.log(`Checking if warehouse is linked to branches: ${!branchSnapshot.empty}`);

      if (!branchSnapshot.empty) {
        console.log(`Found ${branchSnapshot.size} branches using this warehouse`);
        throw new Error('لا يمكن حذف المخزن لأنه مرتبط بفرع. يجب إزالة ارتباط المخزن من الفرع أولاً.');
      }
    } else {
      console.log('Force delete is ON, skipping constraints checks');
    }

    // حذف المخزن
    const warehouseRef = doc(ensureFirestore(), 'warehouses', warehouseId);
    await deleteDoc(warehouseRef);
    console.log(`Warehouse ${warehouseId} deleted successfully`);

    // تحديث المنتجات التي تشير إلى هذا المخزن
    const inventoryCollection = getInventoryCollection();
    const inventoryQuery = query(inventoryCollection);
    const inventorySnapshot = await getDocs(inventoryQuery);

    for (const docSnapshot of inventorySnapshot.docs) {
      const data = docSnapshot.data();
      const warehouseQuantities = data.warehouseQuantities || [];

      // إزالة المخزن من قائمة المخازن في المنتج
      const updatedQuantities = warehouseQuantities.filter(
        (wq: any) => wq.warehouseId !== warehouseId
      );

      if (updatedQuantities.length !== warehouseQuantities.length) {
        await updateDoc(docSnapshot.ref, {
          warehouseQuantities: updatedQuantities
        });
        console.log(`Updated inventory item ${docSnapshot.id} to remove warehouse reference`);
      }
    }

  } catch (error) {
    console.error('Error deleting warehouse:', error);
    throw error;
  }
};

// Export all functions
export default {
  getAllInventoryItems,
  getInventoryItemsByCategory,
  getLowStockItems,
  getInventoryItemById,
  addInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  updateInventoryQuantity,
  searchInventoryItems,
  getInventoryCategories,
  updateInventoryItemQuantity,
  transferInventoryItem,
  getInventoryTransactions,
  getTotalInventoryValue,
  uploadInventoryImage,
  updateInventoryItemsCategory,
  clearCategoryFromInventory,
  updateWarehouse,
  createWarehouse,
  getAllWarehouses,
  getWarehouseById,
  deleteWarehouse,
  deleteAllWarehouses,
  createDefaultWarehouse,
  resetWarehouses,
  checkUserWarehouseAccess,
  updateInventoryQuantityInWarehouse,
  updateInventoryQuantityInWarehouseWithAccess,
  transferInventoryItemWithAccess
};